import { useEffect, useState, useContext, useCallback } from "react";
import { GraphicalViewerMapContext } from "../admin/context/GraphicalViewerMapContext";
import { GraphicalViewerContext } from "../contexts/GraphicalViewerContext";
import TractParser from "../utilities/TractParser";
import hexToRGB from "../utilities/hexToRGB";

export function useMapView() {
    const {
        files,
        currentMapFile,
        currentColorizedFile,
        colorizedFiles,
        setGraphicalViewerMapContext,
    } = useContext(GraphicalViewerMapContext);
    const { completionsData, completionIndex } = useContext(
        GraphicalViewerContext
    );
    const [mapFiles, setMapFiles] = useState(null);
    const [fileIndex, setFileIndex] = useState(-1);

    const selectNewFile = useCallback(
        (index) => {
            if (!mapFiles || !mapFiles[completionIndex]) return;

            const currentCompletionFiles = mapFiles[completionIndex];
            if (index < 0) index = currentCompletionFiles.length - 1;
            if (index >= currentCompletionFiles.length) index = 0;

            if (index === fileIndex) return;

            const mapFile = currentCompletionFiles[index];
            const originalFile = files.find((f) => f.id === mapFile.id);

            if (originalFile && mapFile) {
                setGraphicalViewerMapContext((prev) => {
                    const colorizedFile = prev.colorizedFiles.find(
                        (img) => img.id === originalFile.id
                    );
                    return {
                        ...prev,
                        currentMapFile: {
                            ...originalFile,
                            preview: mapFile.preview, // Use the colorized preview
                        },
                        graphicalViewerMapShowing: true,
                        currentColorizedFile: colorizedFile
                            ? colorizedFile
                            : null,
                    };
                });
                setFileIndex(index);
            }
        },
        [
            mapFiles,
            fileIndex,
            completionIndex,
            files,
            setGraphicalViewerMapContext,
        ]
    );

    // generate the map files when the completions data or colorized files change
    const refreshMapFiles = useCallback(async () => {
        if (!colorizedFiles) return;

        // If there are no completionsData, just show the base colorizedFiles
        if (!completionsData || completionsData.length === 0) {
            const mapFiles = colorizedFiles.map((file) => ({
                id: file.id,
                preview: file.preview,
            }));
            setMapFiles(mapFiles);
        } else {
            const mapFiles = await Promise.all(
                completionsData.map(async (completion) => {
                    return Promise.all(
                        colorizedFiles.map(async (file) => {
                            const nonColorized = files.find(
                                (f) => f.id === file.id
                            );
                            const colorizedFile = await colorTracts(
                                file.graphicalViewerData,
                                createFillTargets(completion),
                                nonColorized.preview,
                                file.preview
                            );
                            return {
                                id: file.id,
                                preview: colorizedFile,
                            };
                        })
                    );
                })
            );
            setMapFiles(mapFiles);
        }
    }, [completionsData, colorizedFiles, files]);

    // initialize the map file when the component mounts and all the data is loaded
    useEffect(() => {
        if (
            mapFiles &&
            mapFiles.length > 0 &&
            mapFiles[completionIndex] &&
            mapFiles[completionIndex].length > 0 &&
            !currentMapFile &&
            fileIndex === -1
        ) {
            selectNewFile(0);
        }
    }, [
        currentMapFile,
        currentColorizedFile,
        fileIndex,
        mapFiles,
        completionIndex,
        selectNewFile,
    ]);

    // refresh the map files when the completions data or colorized files change
    useEffect(() => {
        if (!completionsData || !colorizedFiles) return;
        refreshMapFiles();
    }, [colorizedFiles, completionsData, refreshMapFiles]);

    // set the current map file when the completion index or file index changes
    useEffect(() => {
        if (!mapFiles || !mapFiles[completionIndex]) return;

        if (completionsData.length === 0) {
            setGraphicalViewerMapContext((prev) => ({
                ...prev,
                currentMapFile: {
                    preview: mapFiles[completionIndex].preview,
                },
            }));
        } else {
            setGraphicalViewerMapContext((prev) => ({
                ...prev,
                currentMapFile: mapFiles[completionIndex][fileIndex],
            }));
        }
    }, [
        completionIndex,
        completionsData.length,
        fileIndex,
        mapFiles,
        setGraphicalViewerMapContext,
    ]);

    return {
        fileIndex,
        selectNewFile,
        currentMapFile,
    };
}

// -- Utility Functions --

function createFillTargets(completion) {
    const fillTargets = [];

    if (completion?.bids?.length > 0) {
        completion.bids.forEach((bid) => {
            const tracts = TractParser(bid.bid_name).tractArray;
            const bidderColor = hexToRGB(bid.bidder.bidder_color);

            tracts.forEach((tractStr) => {
                const tract = parseInt(tractStr, 10);
                if (!isNaN(tract)) {
                    fillTargets.push({
                        tract: tract,
                        color: {
                            r: bidderColor.r,
                            g: bidderColor.g,
                            b: bidderColor.b,
                            a: 255,
                        },
                    });
                }
            });
        });
    }

    return fillTargets;
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

async function colorTracts(
    tractData,
    fillTargets,
    baseImageUrl,
    colorizedImageUrl
) {
    const [baseImg, colorizedImg] = await Promise.all([
        loadImage(baseImageUrl),
        loadImage(colorizedImageUrl),
    ]);

    const width = baseImg.width;
    const height = baseImg.height;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const baseCtx = canvas.getContext("2d");
    const colorizedCtx = canvas.getContext("2d");
    baseCtx.willReadFrequently = true;
    colorizedCtx.willReadFrequently = true;

    baseCtx.drawImage(baseImg, 0, 0, width, height);
    const baseData = baseCtx.getImageData(0, 0, width, height);

    colorizedCtx.drawImage(colorizedImg, 0, 0, width, height);
    const colorizedData = colorizedCtx.getImageData(0, 0, width, height);

    // Create a map of tract RGB values to fill colors
    const fillMap = new Map();
    for (const { tract, color } of fillTargets) {
        if (!tractData) continue;
        const tractEntry = tractData?.find((t) => t.tract === tract);
        if (!tractEntry) continue;
        const key = `${tractEntry.color.r},${tractEntry.color.g},${tractEntry.color.b}`;
        fillMap.set(key, color);
    }

    // Loop through all pixels and replace any matching tract color
    for (let i = 0; i < colorizedData.data.length; i += 4) {
        const r = colorizedData.data[i];
        const g = colorizedData.data[i + 1];
        const b = colorizedData.data[i + 2];
        const key = `${r},${g},${b}`;
        if (fillMap.has(key)) {
            const { r: fr, g: fg, b: fb, a } = fillMap.get(key);
            baseData.data[i] = fr;
            baseData.data[i + 1] = fg;
            baseData.data[i + 2] = fb;
            baseData.data[i + 3] = a ?? 255;
        }
    }

    baseCtx.putImageData(baseData, 0, 0);
    return canvas.toDataURL();
}
